import { SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  Progress,
} from 'antd';
import React, { useEffect, useState } from 'react';
import locale from 'antd/es/date-picker/locale/vi_VN';
import dayjs from 'dayjs';
import reportApi from '../../api/report';

const { Text } = Typography;

const DriverTime = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(true);
  const [filter, setFilter] = useState({
    month: dayjs().month() + 1, // dayjs month is 0-based, API expects 1-based
    year: dayjs().year(),
  });
  const [driverStats, setDriverStats] = useState({
    drivers: [],
    total_drivers: 0,
    total_standby_minutes: 0,
    total_working_minutes: 0,
    total_minutes: 0,
  });

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const res = await reportApi.getDriverOnlineStats(filter);
        
        // Handle different possible response structures
        let driverData = null;
        if (res && res.data) {
          // If response has nested data structure
          driverData = res.data;
        } else if (res && Array.isArray(res)) {
          // If response is directly an array
          driverData = {
            drivers: res,
            total_drivers: res.length,
            total_standby_minutes: res.reduce((sum, driver) => sum + (driver.total_standby_minutes || 0), 0),
            total_working_minutes: res.reduce((sum, driver) => sum + (driver.total_working_minutes || 0), 0),
            total_minutes: res.reduce((sum, driver) => sum + (driver.total_minutes || 0), 0),
          };
        } else if (res && res.drivers) {
          // If response has drivers property directly
          driverData = res;
        } else {
          // Default structure
          driverData = {
            drivers: [],
            total_drivers: 0,
            total_standby_minutes: 0,
            total_working_minutes: 0,
            total_minutes: 0,
          };
        }
        
        setDriverStats(driverData);
      } catch (error) {
        console.error('API Error:', error);
        // Set default values on error
        setDriverStats({
          drivers: [],
          total_drivers: 0,
          total_standby_minutes: 0,
          total_working_minutes: 0,
          total_minutes: 0,
        });
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [filter, reload]);

  const onFilter = () => {
    form.validateFields().then((value) => {
      const selectedDate = value?.date_from || dayjs();
      const data = {
        month: selectedDate.month() + 1, // dayjs month is 0-based, API expects 1-based
        year: selectedDate.year(),
      };
      setFilter(data);
    });
  };

  const disabledDate = (current) => {
    // Can not select days before today and today
    return current && current > dayjs().endOf('day');
  };

  const onReload = () => {
    setReload((e) => !e);
  };

  const handleChangeSize = (pageSize) => {
    setFilter((item) => ({
      ...item,
      limit: pageSize,
    }));
  };

  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const formatPercentage = (value, total) => {
    if (total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Row justify={'end'}>
        <Col>
          <Button type="primary" onClick={onReload}>
            Làm mới
          </Button>
        </Col>
      </Row>
      <Form form={form} onFinish={onFilter}>
        <Row gutter={8} justify={'start'}>
          <Col>
            <Form.Item name="date_from" initialValue={dayjs()}>
              <DatePicker
                disabledDate={disabledDate}
                locale={locale}
                picker="month"
              />
            </Form.Item>
          </Col>
          <Col>
            <Button htmlType="submit" type="primary">
              <SearchOutlined />
            </Button>
          </Col>
        </Row>
      </Form>
      
      {/* Summary Stats */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                {driverStats?.total_drivers || 0}
              </Text>
              <br />
              <Text type="secondary">Tổng tài xế</Text>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                {formatMinutes(driverStats?.total_working_minutes || 0)}
              </Text>
              <br />
              <Text type="secondary">Thời gian làm việc</Text>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                {formatMinutes(driverStats?.total_standby_minutes || 0)}
              </Text>
              <br />
              <Text type="secondary">Thời gian chờ</Text>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
                {formatMinutes(driverStats?.total_minutes || 0)}
              </Text>
              <br />
              <Text type="secondary">Tổng thời gian</Text>
            </div>
          </Col>
        </Row>
      </Card>

      <Row justify={'end'}>
        <Col>
          <Select
            allowClear
            style={{ minWidth: '50px' }}
            onChange={handleChangeSize}
            placeholder="Chọn top..."
            options={[
              { value: 5, label: 'Top 5' },
              { value: 10, label: 'Top 10' },
              { value: 20, label: 'Top 20' },
              { value: 50, label: 'Top 50' },
            ]}
          />
        </Col>
      </Row>
      
      {loading ? (
        <Spin style={{ width: '100%' }} />
      ) : driverStats?.drivers?.length > 0 ? (
        (() => {
          const filteredDrivers = driverStats.drivers
            .filter((item) => item) // Remove null/undefined items
            .sort((a, b) => (b.total_minutes || 0) - (a.total_minutes || 0)); // Sort by total minutes
          
          const limitedDrivers = filter.limit 
            ? filteredDrivers.slice(0, filter.limit)
            : filteredDrivers;
          
          return limitedDrivers.map((item, index) => {
            const workingPercentage = formatPercentage(item.total_working_minutes || 0, item.total_minutes || 1);
            const standbyPercentage = formatPercentage(item.total_standby_minutes || 0, item.total_minutes || 1);
            
            return (
              <Card key={item.driver_id || index} style={{ marginBottom: 8 }}>
                <Row align="middle">
                  <Col span={2}>
                    <Tag
                      color={
                        index === 0
                          ? '#f3d551'
                          : index === 1
                          ? '#a6a6a6'
                          : index === 2
                          ? '#b27941'
                          : null
                      }
                    >{`#${index + 1}`}</Tag>
                  </Col>
                  <Col span={8}>
                    <div>
                      <Text strong>{item.lastname || item.driver_name || 'Unknown Driver'}</Text>
                      <br />
                      
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ textAlign: 'center' }}>
                      <Text strong style={{ color: '#52c41a' }}>
                        {formatMinutes(item.total_working_minutes || 0)}
                      </Text>
                      <br />
                     
                    </div>
                  </Col>
                  <Col span={4}>
                    <div style={{ textAlign: 'center' }}>
                      <Text strong style={{ color: '#faad14' }}>
                        {formatMinutes(item.total_standby_minutes || 0)}
                      </Text>
                      <br />
                      
                    </div>
                  </Col>
                  <Col span={6}>
                    <div style={{ textAlign: 'center' }}>
                      <Text strong style={{ color: '#722ed1', fontSize: '16px' }}>
                        {formatMinutes(item.total_minutes || 0)}
                      </Text>
                      <br />
                      
                    </div>
                  </Col>
                </Row>
                
              </Card>
            );
          });
        })()
      ) : (
        <Empty description="Không có dữ liệu" />
      )}
    </Space>
  );
};

export default DriverTime;
